import React, { useState } from "react";
import { FlyoutContent, WithFlyout } from '@amzn/stencil-react-components/flyout';
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { Input, InputFooter, TextArea } from "@amzn/stencil-react-components/form";
import { useAppSelector } from "src/state/hooks";
import "src/components/commonStyles.scss";

import AceEditor from "react-ace";

import "ace-builds/src-noconflict/mode-java";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/ext-language_tools";
import { IconJumpToContent, IconPlay } from "@amzn/stencil-react-components/icons";
import { executeEvents } from "src/service/service";
import { APICallStatus, Stage } from "src/enum/enums";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { ExecuteEventsResponse } from "src/entity/entities";

const STRINGS_DISPLAYED = {
  test_button: "Test",
  done_button: "Done",
  execute_events_button: "Execute Events",
  flyout_title: "Test your event model",
  event_name_label: "Event Name",
  trigger_label: "Trigger",
  employee_id_label: "Employee id",
  employee_id_placeholder: "Enter an employee id",
  request_context_label: "Request context",
  request_context_parsing_error: "Error parsing request context",
  error: "Error",
  output_label: "Output",
  event_not_eligible_message: "Given event is not eligible for given employee.",
  event_label: "Event",
  solution_type_label: "Solution type",
  solution_details_label: "Solution details",
}

type props = {
  isLiveEventModelPresentInBeta: boolean,
}

/**
 * Renders a button with a flyout, which enables users to test
 * their event models by calling the EASE Execute Events API.
 */
export const TestExecuteEventsButtonWithFlyout = ({isLiveEventModelPresentInBeta}: props) => {
  return <WithFlyout renderFlyout={testExecuteEventsFlyout}>
    {({ open }) => (
      <Button
        variant={ButtonVariant.Primary}
        icon={<IconPlay aria-hidden={true} />}
        className={"width_fit_content"}
        onClick={open}
        disabled={!Boolean(isLiveEventModelPresentInBeta)}
      >
        {STRINGS_DISPLAYED.test_button}
      </Button>
    )}
  </WithFlyout>
}

const testExecuteEventsFlyout = ({ close }) => {
  const eventModelState = useAppSelector(({ eventModel }) => eventModel);

  const [employeeId, setEmployeeId] = useState<string>("");

  const [requestContext, setRequestContext] = useState<string>("{}");
  const [requestContextParsingError, setRequestContextParsingError] = useState();

  const [executeEventsOutput, setExecuteEventsOutput] = useState<ExecuteEventsResponse | Error>();
  const [executeEventsStatus, setExecuteEventsStatus] = useState<APICallStatus>(APICallStatus.SUCCESSFUL);


  const onClickExecuteEvents = async () => {
    let requestContextObject: Record<string, any>;
    try {
      requestContextObject = JSON.parse(requestContext);
      setRequestContextParsingError(undefined);
    } catch(error) {
      setRequestContextParsingError(error);
      return;
    }

    setExecuteEventsStatus(APICallStatus.IN_PROGRESS);
    try {
      const response = await executeEvents(
        eventModelState?.clientUseCase,
        employeeId,
        requestContextObject,
        Stage.beta,
      );
      setExecuteEventsOutput(response);
      setExecuteEventsStatus(APICallStatus.SUCCESSFUL);
    } catch(error) {
      setExecuteEventsOutput(error);
      setExecuteEventsStatus(APICallStatus.FAILED);
    }
  }

  return <FlyoutContent
    titleText={STRINGS_DISPLAYED.flyout_title}
    onCloseButtonClick={close}
    buttons={[
      <Button onClick={close} variant={ButtonVariant.Secondary}>
        {STRINGS_DISPLAYED.done_button}
      </Button>
    ]}>
      <Col gridGap={"8px"} width={"33vw"} overflow={"hidden"} padding={{right: "20px"}}>
        <Row alignItems={"center"} gridGap={"4px"} className={"flex_4"}>
          <Label 
            fontSize={"16px"}
            htmlFor={"event-name-input-flyout"}
            className={"flex_1"}
          >
            {STRINGS_DISPLAYED.event_name_label}
          </Label>
          <Input
            id={"event-name-input-flyout"}
            value={eventModelState?.eventName}
            disabled={true}
            className={"flex_3"}
          />
        </Row>

        <Row alignItems={"center"} gridGap={"4px"} className={"flex_4"}>
          <Label 
            fontSize={"16px"}
            htmlFor={"trigger-input-flyout"}
            className={"flex_1"}
          >
            {STRINGS_DISPLAYED.trigger_label}
          </Label>
          <Input
            id={"trigger-input-flyout"}
            value={eventModelState?.trigger}
            disabled={true}
            className={"flex_3"}
          />
        </Row>

        <Row alignItems={"center"} gridGap={"4px"} className={"flex_4"}>
          <Label
            fontSize={"16px"}
            htmlFor={"employeeid-input-flyout"}
            className={"flex_1"}
          >
            {STRINGS_DISPLAYED.employee_id_label}
          </Label>
          <Input
            id={"employeeid-input-flyout"}
            value={employeeId}
            onChange={(event) => {
              setEmployeeId(event?.target?.value);
            }}
            placeholder={STRINGS_DISPLAYED.employee_id_placeholder}
            className={"flex_3"}
          />
        </Row>

        <Col gridGap={"4px"}>
          <Label fontSize={"16px"} htmlFor={"request-context-input-flyout"}>
            {STRINGS_DISPLAYED.request_context_label}
          </Label>

          <View border={"1px"} id={"request-context-input-flyout"}>
            <AceEditor
              value={requestContext}
              onChange={(value) => {
                setRequestContext(value);
              }}
              mode={"java"}
              width={"100%"}
              height={"150px"}
              theme={"github"}
              fontSize={16}
              name={"event-rule"}
              editorProps={{ $blockScrolling: true }}
            />
          </View>
          {requestContextParsingError && <InputFooter error={true}>
            {`${STRINGS_DISPLAYED.request_context_parsing_error}: ${requestContextParsingError}`}
          </InputFooter>}
        </Col>

        <Button 
          onClick={onClickExecuteEvents} 
          variant={ButtonVariant.Primary} 
          icon={<IconJumpToContent />}
          disabled={executeEventsStatus === APICallStatus.IN_PROGRESS}
        >
          {STRINGS_DISPLAYED.execute_events_button}
        </Button>

        <RenderExecuteEventsOutput 
          executeEventsStatus={executeEventsStatus}
          executeEventsOutput={executeEventsOutput}
          expectedEventName={eventModelState?.eventName}
        />
      </Col>
    </FlyoutContent>
}

type RenderOutputProps = {
  executeEventsStatus: APICallStatus;
  executeEventsOutput: ExecuteEventsResponse | Error;
  expectedEventName: string;
}
const RenderExecuteEventsOutput = ({executeEventsStatus, executeEventsOutput, expectedEventName}: RenderOutputProps) => {
  // display spinner if ExecuteEvents API call is in progress
  if (executeEventsStatus === APICallStatus.IN_PROGRESS) {
    return <Spinner />;
  }

  // display error banner on failed API call
  if (executeEventsStatus === APICallStatus.FAILED) {
    return <Col gridGap={"16px"}>
      <Label fontSize={"18px"} fontWeight={"bold"}>
        {STRINGS_DISPLAYED.error}
      </Label>

      <TextArea
        value={JSON.stringify(executeEventsOutput)}
        disabled={true}
        height={"200px"}
      />
    </Col>
  }

  // when executeEventsOutput is un-initialized
  if (!executeEventsOutput) {
    return <></>;
  }

  // filter the expected event from ececuteEvents output
  const eligibleEvent = (executeEventsOutput as ExecuteEventsResponse).events?.filter((event) => {
    return event?.eventName === expectedEventName;
  })[0];

  // if the expectedEvent was not processed by EASE
  if (!eligibleEvent) {
    return <Col gridGap={"16px"}>
      <Label fontSize={"18px"} fontWeight={"bold"}>
        {STRINGS_DISPLAYED.output_label}
      </Label>

      <Text>{STRINGS_DISPLAYED.event_not_eligible_message}</Text>
    </Col>
  }

  // display output when the expected event is executed by EASE
  return <Col gridGap={"16px"}>
    <Label fontSize={"18px"} fontWeight={"bold"}>
      {STRINGS_DISPLAYED.output_label}
    </Label>

    <Col gridGap={"8px"}>
      <Row gridGap={"4px"}>
        <Label fontSize={"16px"} color={"grey"}>
          {STRINGS_DISPLAYED.event_label}:
        </Label>
        <Text fontSize={"16px"}>{eligibleEvent?.eventName}</Text>
      </Row>

      <Row gridGap={"4px"}>
        <Label fontSize={"16px"} color={"grey"}>
          {STRINGS_DISPLAYED.solution_type_label}:
        </Label>
        <Text fontSize={"16px"}>{eligibleEvent?.eventSolution?.type}</Text>
      </Row>

      <Col gridGap={"6px"}>
        <Label fontSize={"16px"} color={"grey"}>
          {STRINGS_DISPLAYED.solution_details_label}:
        </Label>
        <TextArea
          value={JSON.stringify(eligibleEvent?.eventSolution?.solutionDetail, null, 2)}
          disabled={true}
          height={"200px"}
        />
      </Col>
    </Col>
  </Col>
}

